// Tela de Cadastro de Tipo de Peça (issue #4, revista no ADR-0006). Só nome +
// categoria + situação — o preço mora na Tabela de Preços.

import type { TipoPeca } from "../../domain/snapshot.ts";
import type { Store } from "../store.ts";
import { clear, el, tabelaPaginada, botao } from "../ui.ts";
import { barraCadastro, telaLista } from "./_cadastro.ts";
import {
  abrirGerenciarCategorias,
} from "./_categoria-select.ts";
import { abrirFormTipoPeca } from "./_tipo-peca-form.ts";
import { toastErro } from "../toast.ts";
import { EstadoLista } from "./_estado-lista.ts";
import { buscaDaLista, filtroMultiplo, SITUACAO_CADASTRO } from "./_lista-filtros.ts";
import { podeEditarModulo } from "./_permissao.ts";

export function renderTiposPeca(
  container: HTMLElement,
  store: Store,
): () => void {
  // filtros, busca e página num estado só (#93): null = todos; ordena por Categoria
  const lista = new EstadoLista({ categoria: null as string[] | null, situacao: null as string[] | null }, { ordCol: 1 });
  const busca = buscaDaLista(lista, "Buscar por nome…", () => render());
  const fSituacao = filtroMultiplo(lista, "situacao", "Situação", () => render());
  const fCategoria = filtroMultiplo(lista, "categoria", "Categoria", () => render());

  const render = (): void => {
    clear(container);
    const snap = store.get();
    const podeGravar = podeEditarModulo(store, "configuracao");
    const catNome = new Map(snap.categorias.map((c) => [c.id, c.nome]));

    fCategoria.atualizar([...snap.categorias]
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))
      .map((c) => ({ id: c.id, nome: c.nome })));
    fSituacao.atualizar(SITUACAO_CADASTRO);
    const catFiltro = fCategoria.selecionados();
    const sitFiltro = fSituacao.selecionados();
    const tipos = [...snap.tiposPeca]
      .filter((t) => catFiltro.size === 0 || catFiltro.has(t.categoriaId))
      .filter((t) =>
        sitFiltro.size === 0 || sitFiltro.has(t.ativo ? "ativo" : "inativo"),
      )
      .filter((t) => busca.casa(t.nome, catNome.get(t.categoriaId)))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    const gerenciarCats = botao(
      "secundario",
      {},
      "Gerenciar Categorias",
    );
    gerenciarCats.addEventListener("click", () =>
      abrirGerenciarCategorias(store),
    );

    const barra = barraCadastro(
      [
        fCategoria.element,
        fSituacao.element,
        ...(podeGravar ? [gerenciarCats] : []),
      ],
      "Novo Tipo de Peça",
      () => {
        if (snap.categorias.some((c) => c.ativo)) {
          abrirFormTipoPeca(store, null);
        } else {
          toastErro("Cadastre uma categoria ativa antes dos tipos de peça.");
        }
      },
      () => {
        lista.limpar();
        render();
      },
      undefined,
      podeGravar,
    );

    if (tipos.length === 0) {
      container.append(
        telaLista(
          barra,
          busca.elemento(),
          el(
            "p",
            { class: "muted lista-vazia" },
            lista.mensagemVazio({ nenhum: "Nenhum tipo de peça.", filtrado: "Nenhum tipo de peça encontrado." }),
          ),
        ),
      );
      return;
    }

    const linhas = tipos.map((t) => {
      const acoes: Node[] = [];
      if (podeGravar) {
        const editar = botao("secundario", {}, "Editar");
        editar.addEventListener("click", () => abrirFormTipoPeca(store, t));
        const alternar = botao(
          "secundario",
          {},
          t.ativo ? "Inativar" : "Reativar",
        );
        alternar.addEventListener("click", () => {
          void store.executar(
            {
              agregado: "tipoPeca",
              cmd: { tipo: "definirAtivo", id: t.id, ativo: !t.ativo },
            },
            t.ativo ? "Tipo de peça inativado." : "Tipo de peça reativado.",
          );
        });
        acoes.push(editar, alternar);
      }
      return el(
        "tr",
        { class: t.ativo ? "" : "inativo" },
        el("td", {}, t.nome),
        el("td", { class: "muted" }, catNome.get(t.categoriaId) ?? "—"),
        el(
          "td",
          {},
          t.ativo
            ? el("span", { class: "estado-ativo" }, "Ativo")
            : el("span", { class: "estado-inativo" }, "Inativo"),
        ),
        el("td", { class: "acoes" }, ...acoes),
      );
    });
    container.append(
      telaLista(
        barra,
        busca.elemento(),
        tabelaPaginada(
          "tipos-peca",
          ["Nome", "Categoria", "Situação", ""],
          linhas,
          lista.tabela,
          render,
        ),
      ),
    );
  };

  render();

  const alvoEdit = new URLSearchParams(location.hash.split("?")[1] ?? "").get(
    "edit",
  );
  if (alvoEdit && podeEditarModulo(store, "configuracao")) {
    const t: TipoPeca | undefined = store
      .get()
      .tiposPeca.find((x) => x.nome.toLowerCase() === alvoEdit.toLowerCase());
    if (t) abrirFormTipoPeca(store, t);
  }

  return store.subscribe(render);
}
